import { createApiClient } from "@magarac/shared/api-client";
import { isApiError } from "@magarac/shared/errors";
import {
  bookingConfigSchema,
  publicBookingResultSchema,
  publicHoldSchema,
  publicSlotsSchema,
  type PublicBookInput,
  type PublicHoldInput,
  type PublicSlotsInput,
} from "@magarac/shared/schemas/booking";

/** Same origin: the booking Worker (or the Vite proxy locally) forwards /v1 to the api. */
const client = createApiClient({ baseUrl: "" });

export const bookingApi = {
  config: (slug: string) => client.get(`/public/book/${slug}/config`, bookingConfigSchema),
  slots: (slug: string, input: PublicSlotsInput) =>
    client.post(`/public/book/${slug}/slots`, input, publicSlotsSchema),
  hold: (slug: string, input: PublicHoldInput) =>
    client.post(`/public/book/${slug}/hold`, input, publicHoldSchema),
  book: (slug: string, input: PublicBookInput) =>
    client.post(`/public/book/${slug}/book`, input, publicBookingResultSchema),
};

/** What went wrong, in words a customer understands, and whether picking a time again fixes it. */
export function describeError(err: unknown): {
  message: string;
  pickAgain: boolean;
  closed: boolean;
} {
  if (isApiError(err)) {
    switch (err.code) {
      case "hold_conflict":
      case "hold_expired":
      case "hold_not_found":
        return { message: err.message, pickAgain: true, closed: false };
      case "account_paused":
        return { message: err.message, pickAgain: false, closed: true };
      case "not_found":
        return {
          message: "That is no longer available here. Reload the page to see what can be booked.",
          pickAgain: false,
          closed: false,
        };
      case "validation_failed":
      case "conflict":
      case "rate_limited":
      case "forbidden":
        return { message: err.message, pickAgain: false, closed: false };
      default:
        return {
          message: "Something went wrong on our side. Please try again in a moment.",
          pickAgain: false,
          closed: false,
        };
    }
  }
  return {
    message: "We could not reach the booking service. Check your connection and try again.",
    pickAgain: false,
    closed: false,
  };
}
